import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import * as Excel from 'exceljs';
import { saveAs } from 'file-saver';

import { SynopticNodeData } from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.model';
import { SynopticStandardUtils } from '@connect-services/client/components/synoptic/synoptic-graph/synoptic-standard-color.utils';
import { ColorEnum } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.constants';
import { SynopticPlanItem } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { ColorableColumn, DefinitionColumn } from '@connect-services/client/models/export-xlsx.model';
import { PlanTypeEnum } from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';

@Injectable({
    providedIn: 'root',
})
export class ExportPlanService {
    private readonly translocoService = inject(TranslocoService);
    private readonly WIDTH = { DEFAULT: 17, SUMMARY: 50, WIDE: 22 };
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly colorKeyMap: Record<string, keyof SynopticPlanItem> = {
        couleur_fibre_entrante: 'numero_fibre_entrante',
        couleur_fibre_sortante: 'numero_fibre_sortante',
        couleur_tube_entrant: 'numero_tube_entrant',
        couleur_tube_sortant: 'numero_tube_sortant',
    };
    private readonly generalColumnsEnd: DefinitionColumn<
        keyof SynopticPlanItem | 'couleur_fibre_sortante' | 'couleur_tube_sortant'
    >[] = [
        { header: this.translocoService.translate('synopticPlan.cassette'), key: 'cassette' },
        { header: this.translocoService.translate('synopticPlan.cassetteNumber'), key: 'numero_cassette' },
        { header: this.translocoService.translate('synopticPlan.positionNumber'), key: 'numero_position' },
        {
            header: this.translocoService.translate('synopticPlan.connectorization'),
            key: 'connectorisation',
            width: this.WIDTH.WIDE,
        },
        { header: this.translocoService.translate('synopticPlan.spliceType'), key: 'type_epissurage' },
        { header: this.translocoService.translate('synopticPlan.outgoingFiber'), key: 'fibre_sortante' },
        { header: this.translocoService.translate('synopticPlan.outgoingFiberColor'), key: 'couleur_fibre_sortante' },
        { header: this.translocoService.translate('synopticPlan.outgoingFiberType'), key: 'type_fibre_sortante' },
        { header: this.translocoService.translate('synopticPlan.outgoingTubeColor'), key: 'couleur_tube_sortant' },
        { header: this.translocoService.translate('synopticPlan.outgoingTubeNumber'), key: 'numero_tube_sortant' },
        { header: this.translocoService.translate('synopticPlan.outgoingCable'), key: 'cable_etiquet_sortant' },
        { header: this.translocoService.translate('synopticPlan.outgoingBoxBay'), key: 'boite_baie_etiquet_sortante' },
    ];
    private readonly generalColumnsStart: DefinitionColumn[] = [
        { header: this.translocoService.translate('synopticPlan.incomingBoxBay'), key: 'boite_baie_etiquet_entrante' },
        { header: this.translocoService.translate('synopticPlan.incomingCable'), key: 'cable_etiquet_entrant' },
        { header: this.translocoService.translate('synopticPlan.incomingTubeNumber'), key: 'numero_tube_entrant' },
        { header: this.translocoService.translate('synopticPlan.incomingTubeColor'), key: 'couleur_tube_entrant' },
        { header: this.translocoService.translate('synopticPlan.incomingFiberType'), key: 'type_fibre_entrante' },
        { header: this.translocoService.translate('synopticPlan.incomingFiberColor'), key: 'couleur_fibre_entrante' },
        { header: this.translocoService.translate('synopticPlan.incomingFiber'), key: 'fibre_entrante' },
    ];
    private readonly summaryBaySheet: DefinitionColumn<keyof SynopticNodeData>[] = [
        { header: this.translocoService.translate('synopticPlan.logicalTypeBox'), key: 'logicalCodeType' },
        { header: this.translocoService.translate('synopticPlan.physicalTypeSite'), key: 'typephyParent' },
        { header: this.translocoService.translate('synopticPlan.bayLabel'), key: 'boxLabel' },
        { header: this.translocoService.translate('synoptic.graceId'), key: 'graceIdentifier' },
        { header: this.translocoService.translate('synopticPlan.externalBayCode'), key: 'externalCode' },
        { header: this.translocoService.translate('synoptic.address'), key: 'address' },
        { header: this.translocoService.translate('advancement'), key: 'advancement' },
        { header: this.translocoService.translate('synopticPlan.drawerNumber'), key: 'tiroir' },
    ];
    private readonly summaryBoxSheet: DefinitionColumn<keyof SynopticNodeData>[] = [
        { header: this.translocoService.translate('synopticPlan.logicalTypeBox'), key: 'typeCode' },
        { header: this.translocoService.translate('synopticPlan.physicalTypeBox'), key: 'type' },
        { header: this.translocoService.translate('synopticPlan.ptechExternalCode'), key: 'codeExt' },
        { header: this.translocoService.translate('synoptic.technicalPointType'), key: 'technicalPointType' },
        { header: this.translocoService.translate('synoptic.boxLabel'), key: 'boxLabel' },
        { header: this.translocoService.translate('synoptic.graceId'), key: 'graceIdentifier' },
        { header: this.translocoService.translate('synoptic.externalBox'), key: 'externalCode' },
        { header: this.translocoService.translate('synoptic.address'), key: 'address' },
        {
            header: this.translocoService.translate('synoptic.numberVirtualRealCassettes'),
            key: 'numberVirtualRealCassettes',
        },
        { header: this.translocoService.translate('synoptic.numberIncomingFibers'), key: 'incomingFibersByFunction' },
        { header: this.translocoService.translate('synoptic.numberOutgoingFibers'), key: 'outgoingFibersByFunction' },
        { header: this.translocoService.translate('advancement'), key: 'advancement' },
        { header: this.translocoService.translate('synoptic.nbPto'), key: 'nbPto' },
    ];

    public async planToExcel(planType: PlanTypeEnum, plan: SynopticNodeData, data: SynopticPlanItem[]): Promise<void> {
        const filename = this.generatePlanFileName(plan);

        const file = new Excel.Workbook();

        this.createSummarySheet(file, plan, planType);
        this.createPlanSheet(file, data, planType, filename);

        const buffer = await file.xlsx.writeBuffer();
        const sheetType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        const blob = new Blob([buffer], { type: sheetType });

        saveAs(blob, filename);
    }

    private colorsRowsAfterHeader(
        data: SynopticPlanItem[],
        columns: Partial<Excel.Column>[],
        sheet: Excel.Worksheet,
    ): void {
        const coloredColumns: ColorableColumn[] = this.getColoredColumns(columns, sheet);
        const startRowIndex = 2;

        coloredColumns
            .filter(({ key: colorKey }: ColorableColumn) => !!this.colorKeyMap[colorKey])
            .forEach(({ letter, key: colorKey }: ColorableColumn) => {
                const numberKey = this.colorKeyMap[colorKey];

                for (let i = startRowIndex; i < data.length + startRowIndex; i++) {
                    const rowData = data[i - startRowIndex];
                    const rawValue = rowData[numberKey];

                    const emptyValues: unknown[] = [null, undefined, ''];
                    if (emptyValues.includes(rawValue)) {
                        continue;
                    }

                    const color = SynopticStandardUtils.formatColor(Number(rawValue));

                    if (this.isValidHexColorCode(color)) {
                        const cell = `${letter}${i}`;
                        const excelCell = sheet.getCell(cell);

                        excelCell.value = color.toUpperCase();

                        excelCell.fill = {
                            fgColor: { argb: color.slice(1) },
                            pattern: 'solid',
                            type: 'pattern',
                        };

                        if (color.toUpperCase() === ColorEnum.BLACK.toUpperCase()) {
                            excelCell.font = {
                                color: { argb: ColorEnum.WHITE.slice(1) },
                            };
                        }
                    }
                }
            });
    }

    private createPlanSheet(
        file: Excel.Workbook,
        data: SynopticPlanItem[],
        planType: PlanTypeEnum,
        sheetName: string,
    ): Excel.Worksheet | undefined {
        const columns = this.generatePlanSheetColumns(planType);

        if (!columns) {
            return;
        }

        const planSheet = file.addWorksheet(sheetName, { properties: { defaultColWidth: this.WIDTH.DEFAULT } });

        planSheet.columns = columns;

        planSheet.addRows(data);

        this.colorsRowsAfterHeader(data, columns, planSheet);

        return planSheet;
    }

    private createSummarySheet(file: Excel.Workbook, plan: SynopticNodeData, planType: PlanTypeEnum): Excel.Worksheet {
        const sheetName = this.translocoService.translate('synopticPlan.overview');

        const summarySheet = file.addWorksheet(sheetName, {
            properties: { defaultColWidth: this.WIDTH.SUMMARY },
        });

        const summaryRowsSheet: DefinitionColumn<keyof SynopticNodeData>[] =
            planType === PlanTypeEnum.BOX ? this.summaryBoxSheet : this.summaryBaySheet;

        summaryRowsSheet.forEach((colDef: DefinitionColumn<keyof SynopticNodeData>, index) => {
            const rowNumber = index + 1;
            const value = plan[colDef.key];

            summarySheet.getCell(`A${rowNumber}`).value = colDef.header;
            summarySheet.getCell(`B${rowNumber}`).value = value ?? this.translocoService.translate('notAvailable');
        });

        return summarySheet;
    }

    private generatePlanFileName(plan: SynopticNodeData): string {
        const formatedBoxLabel = plan.boxLabel?.replace(/[^a-zA-Z0-9]/g, '_');

        return `${plan.id}_${formatedBoxLabel}.xlsx`;
    }

    private generatePlanSheetColumns(type: PlanTypeEnum): Partial<Excel.Column>[] {
        switch (type) {
            case PlanTypeEnum.BOX: {
                const boxPlanColumns = [{ header: this.translocoService.translate('synopticPlan.box'), key: 'boite' }];

                return this.generalColumnsStart.concat(boxPlanColumns, this.generalColumnsEnd);
            }
            case PlanTypeEnum.BAY: {
                const bayPlanColumns = [
                    { header: this.translocoService.translate('synopticPlan.bay'), key: 'baie' },
                    {
                        header: this.translocoService.translate('synopticPlan.drawerLabel'),
                        key: 'ti_etiquet',
                    },
                    { header: this.translocoService.translate('synopticPlan.drawerSize'), key: 'taille_tiroir' },
                    { header: this.translocoService.translate('synopticPlan.drawerPosition'), key: 'position_tiroir' },
                    { header: this.translocoService.translate('synopticPlan.drawer'), key: 'tiroir' },
                ];

                return this.generalColumnsStart.concat(bayPlanColumns, this.generalColumnsEnd);
            }
            default:
                throw new Error(`Unsupported PlanTypeEnum: ${type}`);
        }
    }

    private getColoredColumns(columns: Partial<Excel.Column>[], sheet: Excel.Worksheet): ColorableColumn[] {
        return columns
            .map((col, index) => {
                AssertService.isString(col.header);
                AssertService.isString(col.key);

                const header = col.header;
                const headerLowerCase = header?.toLowerCase() ?? '';
                const key = col.key;

                if (headerLowerCase.includes('couleur') || headerLowerCase.includes('color')) {
                    const column = sheet.getColumn(index + 1);

                    return {
                        header,
                        key,
                        letter: column.letter ?? '',
                    };
                }

                return null;
            })
            .filter((entry): entry is ColorableColumn => !!entry);
    }

    private isValidHexColorCode(code: string): boolean {
        const hexRegex = /^#([0-9A-F]{3}){1,2}$/i;

        return hexRegex.test(code);
    }
}
